export type TrackKey = "mentee" | "mentor" | "committee";

export const HUB_TRACKS: { key: TrackKey; typeId: number; label: string; singular: string }[] = [
  { key: "mentee", typeId: 1, label: "Mentees", singular: "Mentee" },
  { key: "mentor", typeId: 2, label: "Mentors", singular: "Mentor" },
  { key: "committee", typeId: 3, label: "Committee Members", singular: "Committee Member" },
];

export type StatusKey =
  | "not_submitted"
  | "submitted"
  | "accepted"
  | "waitlisted"
  | "rejected"
  | "declined"
  | "withdrawn";

// Pipeline order: still being written, in review, decided, then dropped out. Pending (1, started)
// and Draft (7, reopened after a withdrawal) both mean "started, not submitted", so they show as one.
export const HUB_STATUSES: { key: StatusKey; ids: number[]; label: string }[] = [
  { key: "not_submitted", ids: [1, 7], label: "Not Submitted" },
  { key: "submitted", ids: [2], label: "Submitted" },
  { key: "accepted", ids: [4], label: "Accepted" },
  { key: "waitlisted", ids: [8], label: "Waitlisted" },
  { key: "rejected", ids: [5], label: "Rejected" },
  { key: "declined", ids: [9], label: "Declined" },
  { key: "withdrawn", ids: [3], label: "Withdrawn" },
];

export const statusKeyOf = (id: number): StatusKey | undefined => HUB_STATUSES.find((s) => s.ids.includes(id))?.key;

// Type and chapter can only change before anyone decides on the application.
export const UNDECIDED_STATUS_IDS = [1, 2, 3, 7];

// The Rater's 0–5 scale; the legacy Applications page labelled an average by rounding it.
const RATING_LABELS = ["Strong No", "No", "Weak No", "Weak Yes", "Yes", "Strong Yes"];
export const ratingCategory = (average: number | null | undefined) =>
  average === null || average === undefined ? "" : (RATING_LABELS[Math.round(average)] ?? "");

// manager_approval_status_id reuses Accepted (4) and Rejected (5).
export const managerApprovalLabel = (id: number | null | undefined) =>
  id === 4 ? "Approved" : id === 5 ? "Declined" : "Waiting";

export interface HubApplication {
  id: number;
  user_id: number;
  name: string | null;
  email: string | null;
  applicant_type_id: number;
  track: TrackKey;
  status_id: number;
  status: string | null;
  submitted_at: string | null;
  manager_approval_status_id: number | null;
  waitlist_position: number | null;
  location: string | null;
  azure_office_location: string | null;
  location_comments: string | null;
  department: string | null;
  title: string | null;
  manager: string | null;
  hire_date: string | null;
  years_with_company: string | number | null;
  ratings: number;
  average: number | null;
  transfer_id: number | null;
  transfer_to_chapter_id: number | null;
  // Reached a completed Host Review afterwards and still needs an accept or reject there.
  late_arrival?: boolean;
}

// Where a moved applicant landed in a Host Review (type change or chapter transfer).
export interface ReviewPlacement {
  session_id: number;
  kind: "queued" | "not_started" | "late_arrival";
  track: TrackKey;
  reopened: boolean;
}

export const placementNote = (review: ReviewPlacement | null | undefined): string => {
  if (!review || review.kind === "not_started") return "";
  const step = HUB_TRACKS.find((t) => t.key === review.track)?.label ?? review.track;
  if (review.kind === "late_arrival") {
    return " The Host Review is already complete, so they need a decision: open Host Review to accept or reject.";
  }
  return review.reopened
    ? ` The open Host Review's ${step} step reopened so the committee can decide them.`
    : ` They join the open Host Review's ${step} step.`;
};

const pad = (n: number) => String(n).padStart(2, "0");

export const exportFilename = (now = new Date()) =>
  `clear_applications_${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.xlsx`;

const dateOnly = (value: string | null) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

// The rows on screen as a workbook: no essays, numeric score beside its category, frozen and
// filterable header, and a link back to each application.
export async function buildApplicationsWorkbook(rows: HubApplication[], chapterName: string, origin: string) {
  const { default: ExcelJS } = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "CLEAR Portal";
  workbook.created = new Date();
  workbook.subject = "Applications";
  const sheet = workbook.addWorksheet("Applications");
  sheet.columns = [
    { header: "Applicant", key: "name" },
    { header: "Email", key: "email" },
    { header: "Chapter", key: "chapter" },
    { header: "Type", key: "type" },
    { header: "Status", key: "status" },
    { header: "Submitted At", key: "submitted", style: { numFmt: "yyyy-mm-dd hh:mm" } },
    { header: "Manager Approval", key: "manager_approval" },
    { header: "Ratings", key: "ratings" },
    { header: "Average Score", key: "average", style: { numFmt: "0.00" } },
    { header: "Rating", key: "category" },
    { header: "Location", key: "location" },
    { header: "Azure Office Location", key: "azure_office_location" },
    { header: "Location Comments", key: "location_comments" },
    { header: "Department", key: "department" },
    { header: "Title", key: "title" },
    { header: "Manager", key: "manager" },
    { header: "Hire Date", key: "hire_date", style: { numFmt: "yyyy-mm-dd" } },
    { header: "Years With Company", key: "years" },
    { header: "Application", key: "link" },
  ];
  const header = sheet.getRow(1);
  header.font = { bold: true };
  header.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD3D3D3" } };
  sheet.views = [{ state: "frozen", ySplit: 1 }];
  sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: sheet.columns.length } };

  for (const r of rows) {
    const url = `${origin}/application/${r.id}`;
    sheet.addRow({
      name: r.name ?? "",
      email: r.email ?? "",
      chapter: chapterName,
      type: HUB_TRACKS.find((t) => t.key === r.track)?.singular ?? "",
      status: r.status ?? "",
      submitted: dateOnly(r.submitted_at),
      manager_approval: managerApprovalLabel(r.manager_approval_status_id),
      ratings: r.ratings,
      average: r.average,
      category: ratingCategory(r.average),
      location: r.location ?? "",
      azure_office_location: r.azure_office_location ?? "",
      location_comments: r.location_comments ?? "",
      department: r.department ?? "",
      title: r.title ?? "",
      manager: r.manager ?? "",
      hire_date: dateOnly(r.hire_date),
      years: r.years_with_company === null ? null : Number(r.years_with_company),
      link: { text: "View", hyperlink: url },
    });
  }

  sheet.columns.forEach((column) => {
    let width = 10;
    column.eachCell?.({ includeEmpty: false }, (cell) => {
      const text = cell.value instanceof Date ? "yyyy-mm-dd hh:mm" : String(cell.text ?? "");
      width = Math.max(width, Math.min(text.length + 2, 60));
    });
    column.width = width;
  });
  return workbook;
}

export async function downloadApplications(rows: HubApplication[], chapterName: string) {
  const workbook = await buildApplicationsWorkbook(rows, chapterName, window.location.origin);
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = exportFilename();
  link.click();
  window.URL.revokeObjectURL(url);
}
